"use client";
import usePriceStats from "@/hooks/use-price-stats";
import { Ref, useEffect, useState } from "react";
import Image from "next/legacy/image";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";
import Link from "next/link";
import { sqmToSqft, sqmToSqyd } from "@/app/utils/area";
import { truncateDecimals } from "@/app/utils/format-number";
import {useRouter} from 'next/navigation';

function numberToReadable(num: number) {
  if (num >= 100000) {
    return (num / 100000).toFixed(1).replace(/\.0$/, "") + "L";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return num.toString();
}
type PropertyType = "plots" | "apartments";

function getColorGradient(value: number, propertyType: PropertyType) {
  if (propertyType === "apartments") {
    if (value > 15000) {
      return "bg-red-800";
    } else if (value > 12000) {
      return "bg-red-600";
    } else if (value > 8000) {
      return "bg-orange-600";
    } else if (value > 6000) {
      return "bg-orange-400";
    } else if (value > 5000) {
      return "bg-yellow-400";
    } else if (value > 4000) {
      return "bg-yellow-200";
    } else if (value > 3000) {
      return "bg-green-200";
    } else if (value > 2000) {
      return "bg-green-400";
    } else if (value > 1000) {
      return "bg-green-600";
    } else {
      return "bg-green-800";
    }
  } else {
    if (value > 120000) {
      return "bg-red-800";
    } else if (value > 80000) {
      return "bg-red-600";
    } else if (value > 60000) {
      return "bg-orange-600";
    } else if (value > 40000) {
      return "bg-orange-400";
    } else if (value > 30000) {
      return "bg-yellow-400";
    } else if (value > 22000) {
      return "bg-yellow-200";
    } else if (value > 16000) {
      return "bg-green-200";
    } else if (value > 10000) {
      return "bg-green-400";
    } else if (value > 8300) {
      return "bg-green-600";
    } else {
      return "bg-green-800";
    }
  }
}

function PriceMarker({
  medianPrice,
  position,
  lowerBound,
  upperBound,
  villageName,
  villageId,
  count,
  color,
  propertyType,
}: {
  medianPrice: number;
  upperBound: number;
  lowerBound: number;
  position: { lat: number; lng: number };
  villageName: string;
  villageId: number;
  count: number;
  color: string;
  propertyType: PropertyType;
}) {
  const router = useRouter();
  return (
    <AdvancedMarker
      position={position}
      className="group"
      collisionBehavior="OPTIONAL_AND_HIDES_LOWER_PRIORITY"
      onClick={() => router.push(`/locality/${villageId}/v2`)}
    >
      <Link href={`/locality/${villageId}/v2`}>
        <div
          className={`${color} text-primary-foreground px-2 py-1 rounded-md`}
        >
          <div className="flex flex-row">
            <div className="text-xs font-medium">
              ₹{numberToReadable(lowerBound)} - ₹{numberToReadable(upperBound)}
            </div>
            <div className="text-xs opacity-50">
              / {propertyType === "apartments" ? "sft" : "sqyd"}
            </div>
          </div>
          <div className="text-xs font-light capitalize">
            {villageName.toLowerCase()}
          </div>
        </div>
      </Link>
    </AdvancedMarker>
  );
}

const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string;

function convertFromSqm(priceData: any, propertyType: PropertyType) {
  if (propertyType === "plots") {
    return {
      ...priceData,
      avg_price_per_sqm: truncateDecimals(
        priceData.avg_price_per_sqm / sqmToSqyd(1),
        0
      ),
      median_price_per_sqm: truncateDecimals(
        priceData.median_price_per_sqm / sqmToSqyd(1),
        0
      ),
      lower_bound: truncateDecimals(priceData.lower_bound / sqmToSqyd(1), 0),
      upper_bound: truncateDecimals(priceData.upper_bound / sqmToSqyd(1), 0),
    };
  } else {
    return {
      ...priceData,
      avg_price_per_sqm: truncateDecimals(
        priceData.avg_price_per_sqm / sqmToSqft(1),
        0
      ),
      median_price_per_sqm: truncateDecimals(
        priceData.median_price_per_sqm / sqmToSqft(1),
        0
      ),
      lower_bound: truncateDecimals(priceData.lower_bound / sqmToSqft(1), 0),
      upper_bound: truncateDecimals(priceData.upper_bound / sqmToSqft(1), 0),
    };
  }
}

export function MapArea() {
  "use client";
  const [propertyType, setPropertyType] = useState("apartments");
  return (
    <div className="md:w-8/12 w-full h-full">
      <div className="flex flex-col h-full">
        <div role="tablist" className="tabs tabs-boxed">
          <a
            role="tab"
            className={`tab ${
              propertyType === "apartments" ? "tab-active" : ""
            }`}
            onClick={() => setPropertyType("apartments")}
          >
            Apartments
          </a>
          <a
            role="tab"
            className={`tab ${propertyType === "plots" ? "tab-active" : ""}`}
            onClick={() => setPropertyType("plots")}
          >
            Plots
          </a>
        </div>
        <ExploreMap propertyType={propertyType as any} />
      </div>
    </div>
  );
}

export function ExploreMap({
  propertyType,
}: {
  propertyType: "plots" | "apartments";
}) {
  const { loading, priceStats } = usePriceStats(propertyType);
  const [mapLoaded, setMapLoaded] = useState(false);
  const map = useMap();
  const heatData = priceStats
    ? priceStats
        .filter((ps: any) => ps.lat && ps.lon && ps.count > 10)
        .map((ps: any) => convertFromSqm(ps, propertyType))
    : [];

  useEffect(() => {}, [propertyType]);

  const center = { lat: 17.391314028266446, lng: 78.46295974371516 };
  return (
    <div className="w-full bg-cover bg-center h-3/4 p-2 flex justify-center">
      <APIProvider apiKey={API_KEY} onLoad={() => setMapLoaded(true)}>
        {mapLoaded && (
          <Map
            defaultZoom={center ? 12 : 8}
            maxZoom={16}
            minZoom={5}
            defaultCenter={center}
            gestureHandling={"cooperative"}
            disableDefaultUI={false}
            mapId={"a0623f86aad6e970"}
            style={{ border: "1px solid LightGray" }}
          >
            {heatData.map((h: any) => (
              <PriceMarker
                position={{ lat: h.lat, lng: h.lon }}
                medianPrice={h.median_price_per_sqm}
                lowerBound={h.lower_bound}
                upperBound={h.upper_bound}
                villageName={h.village_name}
                count={0}
                villageId={h.village_id}
                color={getColorGradient(h.median_price_per_sqm, propertyType)}
                propertyType={propertyType}
              />
            ))}
          </Map>
        )}
      </APIProvider>
      {!mapLoaded && (
        <div className="hidden md:block">
          <Image
            className="display"
            src={`https://maps.googleapis.com/maps/api/staticmap?center=17.391314028266446,78.46295974371516&zoom=7&size=800x500&key=${API_KEY}`}
            alt="Map"
            width={800}
            height={500}
            priority
          />
        </div>
      )}
      {!mapLoaded && (
        <div className="block lg:hidden">
          <Image
            className="display"
            src={`https://maps.googleapis.com/maps/api/staticmap?center=17.391314028266446,78.46295974371516&zoom=7&size=400x500&key=${API_KEY}`}
            alt="Map"
            width={400}
            height={500}
            priority
          />
        </div>
      )}
    </div>
  );
}
